import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workOrderService } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { staffService } from '../../services/staffService';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  FiUsers, FiClipboard, FiCheckCircle,
  FiClock, FiTrendingUp, FiCalendar, FiRefreshCw,
  FiAward, FiWifi, FiWifiOff, FiTruck, FiPlus
} from 'react-icons/fi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const Dashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalOrders: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    delivered: 0,
    paid: 0,
    pendingPayment: 0,
    totalCustomers: 0,
    totalStaff: 0,
    paymentCompletionRate: 0,
    workCompletionRate: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState({});
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // NAVIGASI KARTU
  const handleCardClick = (key) => {
    switch(key) {
      case 'total':
        navigate('/work-orders');
        break;
      case 'pending':
        navigate('/work-orders?status=pending');
        break;
      case 'in-progress':
        navigate('/work-orders?status=in-progress');
        break;
      case 'completed':
        navigate('/work-orders?status=completed');
        break;
      case 'delivered':
        navigate('/work-orders?status=delivered');
        break;
      case 'paid':
        navigate('/work-orders?payment=paid');
        break;
      case 'pendingPayment':
        navigate('/work-orders?payment=pending');
        break;
      case 'customers':
        navigate('/customers');
        break;
      default:
        navigate('/work-orders');
    }
  };

  // NAVIGASI STAFF
  const handleStaffClick = (staffId) => {
    if (staffId) {
      navigate(`/staff/${staffId}`);
    }
  };
  
  // NAVIGASI WO
  const handleWOClick = (woId) => {
    if (woId) {
      navigate(`/work-orders/${woId}`);
    }
  };

  useEffect(() => {
    loadDashboardData();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const interval = setInterval(() => {
      if (isOnline) {
        loadDashboardData();
        setLastUpdated(new Date());
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      const allWO = await workOrderService.getAll() || [];
      const customers = await customerService.getAll() || [];
      const staff = await staffService.getAll() || [];
	  
	   //BUAT MAP CUSTOMER UNTUK AKSES CEPAT
    const customerMap = {};
    customers.forEach(c => {
      customerMap[c.id] = c.name;
    });
      
      //EXCLUDE CANCELLED — HANYA WO AKTIF
      const activeWO = allWO.filter(wo => wo.status !== 'cancelled');
      
      const totalOrders = activeWO.length;
      
      const completed = activeWO.filter(wo => wo.status === 'completed').length;
      const delivered = activeWO.filter(wo => wo.status === 'delivered').length;
      const paid = activeWO.filter(wo => wo.paymentStatus === 'paid').length;
      const pendingPayment = activeWO.filter(wo => 
        (wo.status === 'completed' || wo.status === 'delivered') && 
        wo.paymentStatus === 'pending'
      ).length;
      const inProgress = activeWO.filter(wo => wo.status === 'in-progress').length;
      const pending = activeWO.filter(wo => wo.status === 'pending').length;

      // 2 METRIK COMPLETION RATE (hanya dari WO aktif)
      const paymentCompletionRate = totalOrders > 0 
        ? Math.round((paid / totalOrders) * 100) : 0;

      const workCompletionRate = totalOrders > 0 
        ? Math.round(((completed + delivered) / totalOrders) * 100) : 0;

      setStats({
        totalOrders,
        pending,
        inProgress,
        completed,
        delivered,
        paid,
        pendingPayment,
        totalCustomers: customers.length,
        totalStaff: staff.length,
        paymentCompletionRate,
        workCompletionRate
      });
      
      const sorted = [...allWO].sort((a, b) => 
  new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)
);

//TAMBAH CUSTOMER NAME KE SETIAP ORDER
const recentWithCustomer = sorted.slice(0, 10).map(order => ({
  ...order,
  customerName: customerMap[order.customerId] || '-'
}));
setRecentOrders(recentWithCustomer);

      const months = getLastSixMonths();
      const monthlyOrders = months.map(month => {
        const count = allWO.filter(wo => {
          const woDate = new Date(wo.createdAt || wo.created_at);
          return woDate.getMonth() === month.month && 
                 woDate.getFullYear() === month.year &&
                 wo.status !== 'cancelled'; 
        }).length;
        return {
          month: month.label,
          count: count
        };
      });
      setMonthlyData(monthlyOrders);

      // 🔥 STATUS DISTRIBUTION — EXCLUDE CANCELLED
      setStatusDistribution({
        pending: pending,
        'in-progress': inProgress,
        completed: completed,
        delivered: delivered,
        paid: paid,
        pendingPayment: pendingPayment
      });

      const performance = staff.map(member => {
        const orders = allWO.filter(wo => 
          (wo.staffId === member.id || wo.staff_id === member.id) &&
          wo.status !== 'cancelled' //EXCLUDE CANCELLED
        );
        const completedOrders = orders.filter(wo => 
          wo.status === 'completed' || wo.status === 'delivered'
        );
        return {
          id: member.id,
          name: member.name,
          photo: member.photo || '',
          total: orders.length,
          completed: completedOrders.length,
          rate: orders.length > 0 ? Math.round((completedOrders.length / orders.length) * 100) : 0
        };
      }).sort((a, b) => b.rate - a.rate).slice(0, 5);
      setStaffPerformance(performance);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadDashboardData();
    setLastUpdated(new Date());
    setIsRefreshing(false);
  };

  const getLastSixMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: date.getMonth(),
        year: date.getFullYear(),
        label: date.toLocaleString('id-ID', { month: 'short' })
      });
    }
    return months;
  };

  // LINE CHART
  const lineChartData = {
    labels: monthlyData.map(d => d.month),
    datasets: [{
      label: 'Work Orders',
      data: monthlyData.map(d => d.count),
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      pointBackgroundColor: 'rgb(59, 130, 246)',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
    }]
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'var(--bg-primary)',
        titleColor: 'var(--text-primary)',
        bodyColor: 'var(--text-secondary)',
        borderColor: 'var(--border-color)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: function(context) {
            return `WO: ${context.parsed.y}`;
          }
        }
      },
      title: {
        display: true,
        text: 'Monthly Work Orders',
        color: 'var(--text-primary)',
        font: { size: 14, weight: 'bold' }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, color: 'var(--text-secondary)' },
        grid: { color: '#e5e7eb' }
      },
      x: {
        grid: { display: false },
        ticks: { color: 'var(--text-secondary)' }
      }
    }
  };

  //PIE CHART — EXCLUDE CANCELLED
  const pieChartData = {
    labels: ['Pending', 'In Progress', 'Selesai', 'Dikirim', 'Lunas', 'Belum Bayar'],
    datasets: [{
      data: [
        statusDistribution.pending || 0,
        statusDistribution['in-progress'] || 0,
        statusDistribution.completed || 0,
        statusDistribution.delivered || 0,
        statusDistribution.paid || 0,
        statusDistribution.pendingPayment || 0
      ],
      backgroundColor: [
        'rgba(234, 179, 8, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(168, 85, 247, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 146, 60, 0.8)'
      ],
      borderWidth: 2,
      borderColor: '#fff',
    }]
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'var(--text-primary)',
          padding: 6,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 10 }
        }
      },
      tooltip: {
        backgroundColor: 'var(--bg-primary)',
        titleColor: 'var(--text-primary)',
        bodyColor: 'var(--text-secondary)',
        borderColor: 'var(--border-color)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((context.parsed / total) * 100) : 0;
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      },
      title: {
        display: true,
        text: 'Order Status (Active)',
        color: 'var(--text-primary)',
        font: { size: 13, weight: 'bold' }
      }
    },
    layout: {
      padding: { top: 5, bottom: 5 }
    }
  };

  //STAT CARDS — TANPA CANCELLED
  const statCards = [
    { title: 'Total WO', value: stats.totalOrders, icon: FiClipboard, color: 'bg-blue-500', subtitle: 'Active orders', key: 'total' },
    { title: 'Lunas', value: stats.paid, icon: FiCheckCircle, color: 'bg-emerald-500', subtitle: 'Paid', key: 'paid' },
    { title: 'Belum Bayar', value: stats.pendingPayment, icon: FiClock, color: 'bg-orange-500', subtitle: 'Pending payment', key: 'pendingPayment' },
    { title: 'Customers', value: stats.totalCustomers, icon: FiUsers, color: 'bg-indigo-500', subtitle: 'Registered', key: 'customers' },
    { title: 'Pending', value: stats.pending, icon: FiClock, color: 'bg-yellow-500', subtitle: 'Need attention', key: 'pending' },
    { title: 'In Progress', value: stats.inProgress, icon: FiTrendingUp, color: 'bg-purple-500', subtitle: 'Active', key: 'in-progress' },
    { title: 'Selesai', value: stats.completed, icon: FiCheckCircle, color: 'bg-green-500', subtitle: 'Done', key: 'completed' },
    { title: 'Dikirim', value: stats.delivered, icon: FiTruck, color: 'bg-purple-600', subtitle: 'Delivered', key: 'delivered' },
  ];

  const getStatusColor = (status) => {
    return workOrderService.getStatusColor(status);
  };

  const getStatusLabel = (status) => {
    return workOrderService.getStatusLabel(status);
  };

  const getRankIcon = (index) => {
    const icons = [
      <FiAward key="1" className="text-yellow-500" size={14} />,
      <FiAward key="2" className="text-gray-400" size={14} />,
      <FiAward key="3" className="text-amber-600" size={14} />
    ];
    return index < 3 ? icons[index] : null;
  };

  if (loading) {
  return <LoadingSpinner message="Menghitung statistik... ☕" />;
}

  return (
    <div>
      {/* HEADER — DENGAN TOMBOL NEW WORK ORDER */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Dashboard</h1>
        <div className="flex items-center space-x-3 flex-wrap gap-2">
          {/* TOMBOL NEW WORK ORDER */}
          <button
            onClick={() => navigate('/work-orders/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm hover:shadow-md"
          >
            <FiPlus size={14} />
            <span>New Work Order</span>
          </button>

          
          <span className="text-xs text-[var(--text-secondary)] hidden sm:inline">
            Last updated: {lastUpdated.toLocaleTimeString('id-ID')}
          </span>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <FiRefreshCw size={20} className={`${isRefreshing ? 'animate-spin' : ''} text-[var(--text-primary)]`} />
          </button>
          <span className="text-sm text-[var(--text-secondary)] hidden md:inline">
            <FiCalendar className="inline mr-1" size={16} />
            {new Date().toLocaleDateString('id-ID', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>
      
      {/* STATS GRID - 2 KARTU BESAR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        
        {/* KARTU 1: Keuangan & Customer */}
        <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] p-4">
          <h3 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
            Keuangan & Customer
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {statCards.slice(0, 4).map((card) => {
              const Icon = card.icon;
              return (
                <div 
                  key={card.title}
                  onClick={() => handleCardClick(card.key)}
                  className="p-3 rounded-xl bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-[var(--text-secondary)]">{card.title}</p>
                      <p className="text-lg font-bold text-[var(--text-primary)]">{card.value}</p>
                    </div>
                    <div className={`p-1.5 rounded-full ${card.color} text-white`}>
                      <Icon size={14} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* KARTU 2: Status Pekerjaan */}
        <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] p-4">
          <h3 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
            Status Pekerjaan
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {statCards.slice(4, 8).map((card) => {
              const Icon = card.icon;
              return (
                <div 
                  key={card.title}
                  onClick={() => handleCardClick(card.key)}
                  className="p-3 rounded-xl bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-[var(--text-secondary)]">{card.title}</p>
                      <p className="text-lg font-bold text-[var(--text-primary)]">{card.value}</p>
                    </div>
                    <div className={`p-1.5 rounded-full ${card.color} text-white`}>
                      <Icon size={14} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============================================================
          PROGRESS OVERVIEW — EXCLUDE CANCELLED
          ============================================================ */}
      <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] mb-6">
        <h3 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
          Progress Overview
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Overall Progress */}
          <div className="p-4 bg-[var(--bg-secondary)] rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <FiTrendingUp size={16} className="text-blue-500" />
                  <span>Overall Progress</span>
                </h4>
                <p className="text-xs text-[var(--text-secondary)]">
                  {stats.completed + stats.delivered} of {stats.totalOrders} active orders completed
                </p>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle 
                    className="text-[var(--border-color)]" 
                    strokeWidth="6" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="25" 
                    cx="28" 
                    cy="28"
                  />
                  <circle 
                    className="text-blue-500" 
                    strokeWidth="6" 
                    strokeLinecap="round" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="25" 
                    cx="28" 
                    cy="28"
                    strokeDasharray={`${stats.workCompletionRate * 1.76} 176`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[var(--text-primary)]">
                  {stats.workCompletionRate}%
                </span>
              </div>
            </div>
          </div>

          {/* Work Order Status Distribution*/}
          <div className="p-4 bg-[var(--bg-secondary)] rounded-xl">
            <h4 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-2">
              <FiClipboard size={16} className="text-purple-500" />
              <span>Work Order Status</span>
            </h4>
            <div className="flex items-center justify-between text-xs">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="text-[var(--text-secondary)]">Pending:</span>
                  <span className="font-medium text-[var(--text-primary)]">{stats.pending}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-[var(--text-secondary)]">In Progress:</span>
                  <span className="font-medium text-[var(--text-primary)]">{stats.inProgress}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-[var(--text-secondary)]">Completed:</span>
                  <span className="font-medium text-[var(--text-primary)]">{stats.completed}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="text-[var(--text-secondary)]">Delivered:</span>
                  <span className="font-medium text-[var(--text-primary)]">{stats.delivered}</span>
                </div>
              </div>
              <div className="text-right text-[var(--text-secondary)]">
                <div>Total: <span className="font-medium text-[var(--text-primary)]">{stats.totalOrders}</span></div>
              </div>
            </div>
          </div>

          {/* Team Overview */}
          <div className="p-4 bg-[var(--bg-secondary)] rounded-xl">
            <h4 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-2">
              <FiUsers size={16} className="text-indigo-500" />
              <span>Team Overview</span>
            </h4>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-[var(--text-primary)]">{stats.totalStaff}</span>
                  <span className="text-sm text-[var(--text-secondary)]">Staff</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {stats.totalStaff > 0 ? 'Ready to work' : 'No staff registered'}
                </p>
              </div>
              <div className="flex -space-x-2">
                {staffPerformance.slice(0, 5).map((staff, idx) => (
                  <div 
                    key={idx}
                    className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs border-2 border-white dark:border-gray-800"
                    title={staff.name}
                  >
                    {staff.name?.charAt(0).toUpperCase()}
                  </div>
                ))}
                {stats.totalStaff > 5 && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-[var(--text-secondary)] border-2 border-white dark:border-gray-800">
                    +{stats.totalStaff - 5}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] max-h-64">
          <Line data={lineChartData} options={lineChartOptions} />
        </div>
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] flex items-center justify-center">
          <div className="max-w-[220px] w-full">
            <Pie data={pieChartData} options={pieChartOptions} />
          </div>
        </div>
      </div>

      {/* STAFF PERFORMANCE */}
      {staffPerformance.length > 0 && (
        <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] mb-6">
          <div className="p-4 border-b border-[var(--border-color)] flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Staff Performance</h3>
            <span className="text-xs text-[var(--text-secondary)]">🏆 Ranked by completion rate</span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {staffPerformance.map((staff, index) => {
                const rankIcon = getRankIcon(index);
                const rankNumber = index + 1;
                return (
                  <div 
                    key={index}
                    onClick={() => handleStaffClick(staff.id)}
                    className="text-center p-3 bg-[var(--bg-secondary)] rounded-lg relative cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                  >
                    <div className="absolute top-1 right-2 text-xs font-bold text-[var(--text-secondary)] flex items-center gap-1">
                      {rankIcon || <span className="text-[10px]">#{rankNumber}</span>}
                    </div>
                    <div className="flex justify-center mb-1">
                      {staff.photo ? (
                        <img
                          src={staff.photo}
                          alt={staff.name}
                          className="w-10 h-10 rounded-full object-cover border border-[var(--border-color)] mx-auto"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs border border-[var(--border-color)] mx-auto">
                          {staff.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <p className="font-medium text-[var(--text-primary)] text-sm">{staff.name}</p>
                    <div className="mt-2 flex items-center justify-center space-x-2">
                      <span className="text-sm text-[var(--text-secondary)]">
                        {staff.completed}/{staff.total}
                      </span>
                      <span className={`text-sm font-bold ${
                        staff.rate >= 80 ? 'text-green-600' :
                        staff.rate >= 60 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {staff.rate}%
                      </span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          staff.rate >= 80 ? 'bg-green-600' :
                          staff.rate >= 60 ? 'bg-yellow-600' :
                          'bg-red-600'
                        }`}
                        style={{ width: `${staff.rate}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* RECENT ORDERS */}
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
        <div className="px-4 py-3 border-b border-[var(--border-color)] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Recent Work Orders</h3>
          <span className="text-xs text-[var(--text-secondary)]">Last 10 orders</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">WO</th>
                <th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">Project</th>
				<th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">Customer</th>
                <th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">Status Pekerjaan</th>
                <th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">Payment</th>
                <th className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-left text-[var(--text-secondary)]">Target</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-4 text-center text-[var(--text-secondary)] text-sm">
                    No work orders yet
                  </td>
                </tr>
              ) : (
                recentOrders.slice(0, 10).map((order) => {
                  // 🔥 SKIP CANCELLED DI RECENT ORDERS
                  if (order.status === 'cancelled') return null;
                  return (
                    <tr 
                      key={order.id} 
                      onClick={() => handleWOClick(order.id)}
                      className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer"
                    >
                      <td className="px-3 py-1.5 text-xs font-medium text-left text-[var(--text-primary)]">
                        {order.woNumber}
                      </td>
					  <td className="px-3 py-1.5 text-xs text-left text-[var(--text-primary)] truncate max-w-[120px]">
  {order.projectName}
</td>
<td className="px-3 py-1.5 text-xs text-left text-[var(--text-primary)] truncate max-w-[100px]">
  {order.customerName || '-'}
</td>
<td className="px-3 py-1.5 text-left">
  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${getStatusColor(order.status)}`}>
    {getStatusLabel(order.status)}
  </span>
</td>
                      
                      
                      <td className="px-3 py-1.5 text-left">
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                          order.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                          order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.paymentStatus === 'paid' ? 'Lunas' :
                           order.paymentStatus === 'pending' ? 'Pending' :
                           '-'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-left text-[var(--text-secondary)]">
                        {order.targetDate || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================
          FLOATING ACTION BUTTON — NEW WO
          ============================================================ */}
      <button
        onClick={() => navigate('/work-orders/new')}
        className="fixed bottom-8 right-8 z-50 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
        title="New Work Order"
      >
        <FiPlus size={28} />
      </button>
    </div>
  );
};

export default Dashboard;